import * as localAPI from "localAPI";
import * as languageMap from "shared/languageMap";
import * as utils from "shared/utils";
import PracticeStore from "stores/PracticeStore";
import stores from "stores/stores";
import UserStore from "stores/UserStore";
import UserWordStore from "stores/UserWordStore";
import * as tts from "tts";

type IntroChallengeType = "intro";

export const passiveChallengeTypes = [
  "passiveMultipleChoice",
  "activeMultipleChoice",
  // "pickWord",
  // "arrange",
  // "match",
];

type PassiveChallengeType = (typeof passiveChallengeTypes)[number];

// export const passivePlusChallengeTypes = ["activeMultipleChoice"];

export const activeChallengeTypes = [
  // "makeSentence",
  // There needs to be a separation here. The above are not really active recall, really passive+.
  "typing",
];

type ActiveChallengeType = (typeof activeChallengeTypes)[number];

export type ChallengeType =
  | IntroChallengeType
  | PassiveChallengeType
  | ActiveChallengeType;

export type NextStep =
  | {
      type: "show-feedback";
      correct: "yes" | "no" | "almost";
      message?: {
        title: string;
        body: string;
      };
      showLeniencySettings?: boolean;
      showTypingModeToggle?: boolean;
    }
  | {
      type: "allow-continue";
      // no feedback in this case
    }
  | {
      type: "continue";
    };

export interface ChallengeProps {
  // Generally this will be just one UserWord, but for certain challenge type it will be more
  userWord: UserWordStore;
  allUserWords: PracticeStore;
  skipped?: boolean;
  selectedContextIndex: number;
  introCardMode: "active" | "passive" | "delete";
  completedCallback: (nextStep: NextStep) => void;
  reportRecallEase: (ease: number, difficulty: number) => void;
  enableCheck: (enabled: boolean) => void;
  setCardMode: (introCardMode: "active" | "passive" | "delete") => void;
  // This should be called whenever a challenge shows the explanation
  showingExplanation: () => void;
  // State captured from a prior mount of this challenge, used to restore the
  // widget after the user opens the edit screen and returns. Each challenge
  // type defines its own shape (see getPersistedState on the challenge view).
  persistedState?: unknown;
}

export type ClozeSentencePart = {
  type: "normal" | "target";
  text: string;
};

export type ClozeSentence = ClozeSentencePart[];

export const getClozeSentence = (
  userWord: UserWordStore,
  index: number
): ClozeSentence => {
  const noWordBoundaries =
    languageMap.map[userWord.state.value.language]?.noWordBoundaries;

  const fallbackResult: ClozeSentence = [
    {
      type: "target",
      text: userWord.state.value.word,
    },
  ];

  if (!userWord.state.value.contexts) {
    return fallbackResult;
  }

  const contextText = userWord.state.value.contexts[index]?.text;

  if (!contextText) {
    return [
      {
        type: "normal",
        text: "No context available for ",
      },
      {
        type: "target",
        text: userWord.state.value.word,
      },
      {
        type: "normal",
        text: ".",
      },
    ];
  }

  return buildClozeSentence(
    contextText,
    userWord.state.value.word,
    noWordBoundaries
  );
};

/** Builds a cloze sentence from a text and a regexp. Finds all matching occurrences. */
export const buildClozeSentence = (
  text: string,
  word: string,
  noWordBoundaries: boolean
): ClozeSentence => {
  const regexp = noWordBoundaries
    ? new RegExp(`(${utils.regexpEscape(word)})`, "ig")
    : new RegExp(`\\W(${utils.regexpEscape(word)})\\W`, "ig");
  if (!regexp.global) {
    throw new Error("RegExp must be global");
  }

  const textWithPadding = ` ${text} `;

  const result: ClozeSentence = [];

  // We start at 1 to account for the padding
  let lastIndex = noWordBoundaries ? 0 : 1;

  let match: RegExpExecArray | null;
  while ((match = regexp.exec(textWithPadding)) !== null) {
    if (match.index > lastIndex) {
      result.push({
        type: "normal",
        text: textWithPadding.substring(
          lastIndex,
          match.index + (noWordBoundaries ? 0 : 1)
        ),
      });
    }
    lastIndex = match.index + match[1].length + (noWordBoundaries ? 0 : 1);
    result.push({
      type: "target",
      text: match[1],
    });
  }

  if (lastIndex < textWithPadding.length - (noWordBoundaries ? 0 : 1)) {
    result.push({
      type: "normal",
      text: textWithPadding.substring(
        lastIndex,
        textWithPadding.length - (noWordBoundaries ? 0 : 1)
      ),
    });
  }

  return result;
};

export const playTTS = (language: string, text: string) => {
  tts.play({
    user: stores.user,
    feature: "speakingMode",
    language,
    text,
  });
};

export const normalize = (input: string) =>
  input
    .replace(/[-.,¡!?¿]+/g, " ")
    .replace(/[\u0060\u00b4\u2018\u2019\u201B\u2032\u2035]/g, "'")
    .replace(/[\u201c\u201d]/g, '"')
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

type Grade = "correct" | "typo" | "watch-accent" | "incorrect";

export const gradeTyping = (correct: string, typed: string): Grade => {
  const correctNormalized = normalize(correct);
  const typedNormalized = normalize(typed);

  if (correctNormalized === typedNormalized) {
    return "correct";
  }

  const correctAccentsNormalized = utils.normalizeAccents(correctNormalized);
  const typedAccentsNormalized = utils.normalizeAccents(typedNormalized);

  if (correctAccentsNormalized === typedAccentsNormalized) {
    return "watch-accent";
  }

  const editDistance = utils.levenshteinDistance(
    correctAccentsNormalized,
    typedAccentsNormalized
  );
  const maxPermittedEditDistance = correctNormalized.length > 6 ? 2 : 1;

  if (editDistance <= maxPermittedEditDistance) {
    return "typo";
  }

  return "incorrect";
};

type Leniency = "allowTypos" | "allowMissingAccents" | "strict";

// Temporary leniency storage for prototyping
export const setLeniency = (leniency: Leniency) => {
  localStorage.setItem("leniency", leniency);
};

export const getLeniency = (): Leniency => {
  return (localStorage.getItem("leniency") || "allowTypos") as Leniency;
};

export const hasNewPracticeModes = async (_user: UserStore) => {
  return !(await localAPI.isLocalMode());
};

export const showContextSource = false;

export const cardsPerSession = (user: UserStore) => {
  return {
    verySmall: 4,
    small: 8,
    medium: 15,
    large: 25,
    veryLarge: 50,
  }[user.state.value.practiceLength];
};

export const buildSessionUrl = (
  user: UserStore,
  selectedSources?: string | string[]
) => {
  const sources = Array.isArray(selectedSources)
    ? selectedSources
    : selectedSources
      ? [selectedSources]
      : [];

  if (sources.length === 0) {
    return `/flashcards`;
  }

  if (sources.length === 1 && sources[0].startsWith("book:")) {
    const bookID = sources[0].slice(5);
    return `/${user.currentLearningLanguage.value}/flashcards/book/${bookID}`;
  }

  return `/flashcards?source=${encodeURIComponent(sources.join("|"))}`;
};

export const practiceType = async (
  user: UserStore
): Promise<"new" | "blitz" | "classic"> => {
  return !(await hasNewPracticeModes(user))
    ? "classic"
    : (user.state.value.practiceType as "new" | "classic") ?? "blitz";
};
